import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { POST, POST_CLAIM_RESEND_CS, POST_CLAIM_RESEND_CS_PREVIEW } from '../../../../services';
import {
  CsSendResultTable,
  rowsFromResendPreview,
  rowsFromResendResult,
  showCsSendResult,
} from '../../../../components/cs-send-result';
import { hasCsNumber, SEND_CS_SENT_STATUSES } from '../../shared/sendCsStatus';
const BLOCKED_STATUSES = ['WAIT', 'REJECTED', 'CANCEL'];
const NOT_APPROVED_STATUSES = ['WAIT_APPROVE', 'REJECTED'];

// API /claims/resend-cs และ /resend-cs/preview ต้องมีสิทธิ์นี้ คนที่ไม่มีจะไม่เห็นปุ่ม
const RESEND_CS_PRIVILEGE = 'PE212';

export const useHasResendCsPrivilege = () => {
  const menu = useSelector((state) => state.authen.menu) || [];
  return menu.includes(RESEND_CS_PRIVILEGE);
};

// ขอเลขได้เมื่อยังไม่มีเลข CS และส่งไม่ผ่าน หรือบันทึกว่าส่งแล้วแต่ไม่มีเลข
// (API จะถาม CS ก่อน มีแล้วดึงเลขมาให้ ยังไม่มีค่อยส่งใหม่)
export const canResendCs = (claim) => {
  if (
    NOT_APPROVED_STATUSES.includes(claim?.approveStatus) ||
    BLOCKED_STATUSES.includes(claim?.claimStatus) ||
    hasCsNumber(claim)
  ) {
    return false;
  }
  const status = String(claim?.sendCsStatus || '')
    .trim()
    .toUpperCase();
  return status === 'FAILED' || SEND_CS_SENT_STATUSES.includes(status);
};

// ขอเลขเอกสาร CS: ถาม preview -> ให้ยืนยันว่าจะทำอะไรกับเคลมไหน -> ทำจริง -> แสดงตารางผลลัพธ์
export const useResendCs = ({ modal, onSuccess }) => {
  // ส่งไปทำจริงเฉพาะรายการที่ผู้ใช้ยืนยัน (ไม่ส่งรายการที่ preview บล็อกไว้ไปด้วย
  // เพราะสถานะอาจเปลี่ยนจนกลายเป็นทำได้โดยไม่ผ่านการยืนยัน) แล้วเอารายการที่ถูกบล็อก
  // กลับมาต่อในตารางผลสุดท้ายตามลำดับเดิม ผู้ใช้จะได้เห็นเหตุผลครบทุกเลข
  // ส่ง action ที่ยืนยันไปด้วย API จะทำเฉพาะรายการที่แผนล่าสุดยังตรงกับที่ยืนยัน
  const execute = useMutation({
    mutationFn: ({ confirmedActions }) => POST(POST_CLAIM_RESEND_CS(), { confirmedActions }),
    onSuccess: (response, { previewItems = [] }) => {
      if (onSuccess) onSuccess();
      const executed = new Map((response?.result?.items || []).map((item) => [item.claimId, item]));
      const items = previewItems.map((item) => executed.get(item.claimId) || item);
      showCsSendResult(modal, {
        title: 'ผลการขอเลขเอกสาร CS',
        rows: rowsFromResendResult(items),
        showAction: true,
      });
    },
  });

  const preview = useMutation({
    mutationFn: ({ claimIdList }) => POST(POST_CLAIM_RESEND_CS_PREVIEW(), { claimIdList }),
    onSuccess: (response) => {
      const items = response?.result?.items || [];
      const rows = rowsFromResendPreview(items);
      const actionable = items.filter((item) => item.action === 'RESEND');
      const content = <CsSendResultTable rows={rows} showAction />;

      if (actionable.length === 0) {
        modal.warning({ title: 'ขอเลขเอกสาร CS ไม่ได้', width: 860, okText: 'ตกลง', content });
        return;
      }

      modal.confirm({
        title: 'ยืนยันขอเลขเอกสาร CS',
        width: 860,
        okText: 'ยืนยัน',
        cancelText: 'ยกเลิก',
        content: (
          <div>
            <p>ระบบจะดำเนินการ {actionable.length} รายการ</p>
            {content}
          </div>
        ),
        onOk: () =>
          execute.mutateAsync({
            confirmedActions: actionable.map(({ claimId, action }) => ({ claimId, action })),
            previewItems: items,
          }),
      });
    },
  });

  return {
    startResend: (claimIdList) => preview.mutate({ claimIdList }),
    isPending: preview.isPending || execute.isPending,
  };
};
